import { MoneySource, OfferWizardData, WizardContact } from '../../../../providers/api/dtos';
import { raise } from '../../../../utils';
import { boolToBooleanString, booleanStringToBool } from '../../../../utils/boolean-string/booleanString';
import { LegalEntityAmlFormModel } from '../../../../contact/components/legal-entity-aml-form/LegalEntityAmlForm';
import { NonLegalBuyerAmlFormModel } from './non-legal-buyer-aml-form/NonLegalBuyerAmlForm';

function toMoneySources(antiMoneyLaunderingFormModel: NonLegalBuyerAmlFormModel): MoneySource[] {
  const moneySources = [];
  if (antiMoneyLaunderingFormModel.mortgageMoneySource) {
    moneySources.push(MoneySource.MORTGAGE);
  }
  if (antiMoneyLaunderingFormModel.personalMoneySource) {
    moneySources.push(MoneySource.PERSONAL);
  }
  if (antiMoneyLaunderingFormModel.sellingPersonalPropertiesMoneySource) {
    moneySources.push(MoneySource.SELLING_PERSONAL_PROPERTIES);
  }
  if (antiMoneyLaunderingFormModel.otherMoneySource) {
    moneySources.push(MoneySource.OTHER);
  }
  return moneySources;
}

export const offerWizardBuyerContactDataToNonLegalBuyerAmlFormModel = (buyer: WizardContact): Partial<NonLegalBuyerAmlFormModel> => {
  const { antiMoneyLaundering: aml } = buyer;

  return {
    politicallyExposed: boolToBooleanString(aml?.politicallyExposed),
    relatedToAPoliticallyExposedFigure: boolToBooleanString(aml?.relatedToAPoliticallyExposedFigure),
    mortgageMoneySource: aml?.moneySources?.some((moneySource: MoneySource) => moneySource === MoneySource.MORTGAGE),
    personalMoneySource: aml?.moneySources?.some((moneySource: MoneySource) => moneySource === MoneySource.PERSONAL),
    sellingPersonalPropertiesMoneySource: aml?.moneySources?.some((moneySource: MoneySource) => moneySource === MoneySource.SELLING_PERSONAL_PROPERTIES),
    otherMoneySource: aml?.moneySources?.some((moneySource: MoneySource) => moneySource === MoneySource.OTHER) || false,
    otherMoneySourceValue: aml?.otherMoneySourceValue,
    notes: aml?.notes,
  };
};

export const nonLegalBuyerAmlFormModelToOfferWizardBuyerContactData = (buyer: WizardContact, formModel: NonLegalBuyerAmlFormModel): WizardContact => ({
  ...buyer,
  antiMoneyLaundering: {
    politicallyExposed: booleanStringToBool(formModel.politicallyExposed),
    relatedToAPoliticallyExposedFigure: booleanStringToBool(formModel.relatedToAPoliticallyExposedFigure),
    moneySources: toMoneySources(formModel),
    notes: formModel.notes?.trim() || undefined,
    otherMoneySourceValue: formModel.otherMoneySourceValue?.trim() || undefined,
  },
});

const requireOnlyOneLegalBuyer = (buyers: WizardContact[] = []) => {
  if (buyers.length !== 1 || !buyers[0].legal) {
    raise('there must be only one buyer and it must be legal');
  }
};

export const offerWizardDataToLegalEntityAmlFormModel = (offerWizard: OfferWizardData): Partial<LegalEntityAmlFormModel> => {
  requireOnlyOneLegalBuyer(offerWizard.buyerContacts);

  const isCompanyHolder = typeof offerWizard.excludeAntiMoneyLaundering === 'undefined'
    ? ''
    : boolToBooleanString(!offerWizard.excludeAntiMoneyLaundering);

  return {
    isCompanyHolder,
    notes: offerWizard.buyerContacts?.[0].antiMoneyLaundering?.notes || '',
  };
};

export const legalEntityAmlFormModelToOfferWizardData = (offerWizard: OfferWizardData, formValues: LegalEntityAmlFormModel): OfferWizardData => {
  requireOnlyOneLegalBuyer(offerWizard.buyerContacts);

  const excludeAntiMoneyLaundering = formValues.isCompanyHolder === ''
    ? booleanStringToBool(formValues.isCompanyHolder)
    : !booleanStringToBool(formValues.isCompanyHolder);

  return {
    ...offerWizard,
    buyerContacts: [{
      ...offerWizard.buyerContacts?.[0],
      antiMoneyLaundering: {
        ...offerWizard.buyerContacts?.[0].antiMoneyLaundering,
        notes: formValues.notes.trim() || undefined,
      },
    }],
    excludeAntiMoneyLaundering,
  };
};
